// src/pages/RIMHome.jsx
import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLeaf,
  faHandsHelping,
  faExclamationTriangle,
  faRoad,
  faDog,
  faTrash,
  faBell,
  faUsers,
  faSearch,
  faInfo,
  faFlag,
  faCamera,
  faMapMarkerAlt,
  faClipboard,
  faUserPlus,
  faShareAlt,
  faInfoCircle,
  faChartBar,
  faClock,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";

// Inline SVG for Indian Flag (reliable rendering without emoji issues)
const IndianFlagSVG = ({ className = "inline h-4 w-6" }) => (
  <svg className={className} viewBox="0 0 16 12" xmlns="http://www.w3.org/2000/svg">
    {/* Saffron (top third) */}
    <rect y="0" width="16" height="4" fill="#FF9933" />
    {/* White (middle third) with Ashoka Chakra (simplified blue wheel) */}
    <rect y="4" width="16" height="4" fill="white" />
    <circle cx="8" cy="6" r="1.5" fill="#006BA9" stroke="white" strokeWidth="0.1" />
    {/* Green (bottom third) */}
    <rect y="8" width="16" height="4" fill="#138808" />
  </svg>
);

export default function RIMHome() {
  // Tabs definition
  const rimTabs = [
    { id: "food", label: "🥗 Reduce My Food Waste", icon: faLeaf },
    { id: "donate", label: "🍱 Food Donation Request", icon: faHandsHelping },
    { id: "violation", label: "🚨 I See Violation", icon: faExclamationTriangle },
    { id: "road", label: "🛣️ When I Was on Road", icon: faRoad },
    { id: "dogs", label: "🐶 Street Dogs / Pet Animals", icon: faDog },
    { id: "garbage", label: "🗑️ I Saw Garbage", icon: faTrash },
    { id: "Social Group", label: "👥 Social Group", icon: faTrash },
    { id: "NGO", label: "🏢 NGOs", icon: faTrash },
  ];

  // Shared UI state
  const [activeTab, setActiveTab] = useState(0);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Newest");
  const [toast, setToast] = useState(null);

  // Donation form state
  const [donationForm, setDonationForm] = useState({
    orgName: "",
    need: "",
    quantity: "",
    contact: "",
    location: "",
  });
  const [errors, setErrors] = useState({});

  function submitDonation(e) {
    e.preventDefault();
    if (!validateDonation()) {
      showToast({ type: "error", message: "Please fix errors before submitting." });
      return;
    }
    // Dummy success (no backend)
    setDonationForm({ orgName: "", need: "", quantity: "", contact: "", location: "" });
    showToast({ type: "success", message: "Donation request created (dummy)." });
  }

  // Violation form state
    const [violationForm, setViolationForm] = useState({
    type: "",
    desc: "",
    location: "",
    severity: "",
    contact: "",
    });
    const [violationErrors, setViolationErrors] = useState({});

    // Violation report validation
    function validateViolation() {
    const e = {};
    if (!violationForm.type) e.type = "Type required";
    if (!violationForm.desc.trim()) e.desc = "Description required";
    if (!violationForm.severity) e.severity = "Severity required";
    if (!violationForm.location.trim()) e.location = "Location required";
    if (!/^\d{10}$/.test(violationForm.contact.replace(/\s/g, ''))) e.contact = "Valid 10-digit contact required";
    setViolationErrors(e);
    return Object.keys(e).length === 0;
    }

    function submitViolation(e) {
    e.preventDefault();
    if (!validateViolation()) {
        showToast({ type: "error", message: "Please fix errors before submitting." });
        return;
    }
    // Dummy success (no backend)
    setViolationForm({ type: "", desc: "", location: "", severity: "", contact: "" });
    showToast({ type: "success", message: "Violation report submitted (dummy)." });
    }

  
  
    // Garbage form state
    const [garbageForm, setGarbageForm] = useState({
    type: "",
    qty: "",
    desc: "",
    location: "",
    severity: "",
    contact: "",
    });
    const [garbageErrors, setGarbageErrors] = useState({});

    // Garbage report validation
    function validateGarbage() {
    const e = {};
    if (!garbageForm.type) e.type = "Type required";
    if (!garbageForm.qty.trim()) e.qty = "Quantity required";
    if (!/^\d+$/.test(garbageForm.qty)) e.qty = "Quantity must be a positive number";
    if (!garbageForm.desc.trim()) e.desc = "Description required";
    if (!garbageForm.severity) e.severity = "Severity required";
    if (!garbageForm.location.trim()) e.location = "Location required";
    if (!/^\d{10}$/.test(garbageForm.contact.replace(/\s/g, ''))) e.contact = "Valid 10-digit contact required";
    setGarbageErrors(e);
    return Object.keys(e).length === 0;
    }

    function submitGarbage(e) {
    e.preventDefault();
    if (!validateGarbage()) {
        showToast({ type: "error", message: "Please fix errors before submitting." });
        return;
    }
    // Dummy success (no backend)
    setGarbageForm({ type: "", qty: "", desc: "", location: "", severity: "", contact: "" });
    showToast({ type: "success", message: "Garbage report submitted (dummy)." });
    }

    // Road Tale form state
    const [roadTaleForm, setRoadTaleForm] = useState({
    title: "",
    route: "",
    story: "",
    author: "",
    contact: "",
    });
    const [roadTaleErrors, setRoadTaleErrors] = useState({});

    // Road Tale validation
    function validateRoadTale() {
    const e = {};
    if (!roadTaleForm.title.trim()) e.title = "Title required";
    if (!roadTaleForm.route.trim()) e.route = "Route required";
    if (!roadTaleForm.story.trim()) e.story = "Story required";
    if (!roadTaleForm.author.trim()) e.author = "Author name required";
    if (roadTaleForm.contact && !/^\d{10}$/.test(roadTaleForm.contact.replace(/\s/g, ''))) e.contact = "Valid 10-digit contact required";
    setRoadTaleErrors(e);
    return Object.keys(e).length === 0;
    }

    function submitRoadTale(e) {
    e.preventDefault();
    if (!validateRoadTale()) {
        showToast({ type: "error", message: "Please fix errors before submitting." });
        return;
    }
    // Dummy success (no backend)
    setRoadTaleForm({ title: "", route: "", story: "", author: "", contact: "" });
    showToast({ type: "success", message: "Road tale shared (dummy)." });
    }

  // Animal report form state
  const [animalForm, setAnimalForm] = useState({
    type: "",
    desc: "",
    location: "",
    contact: "",
  });
  const [animalErrors, setAnimalErrors] = useState({});

 // Animal report validation
  function validateAnimal() {
    const e = {};
    if (!animalForm.type) e.type = "Type required";
    if (!animalForm.desc.trim()) e.desc = "Description required";
    if (!animalForm.location.trim()) e.location = "Location required";
    if (!/^\d{10}$/.test(animalForm.contact.replace(/\s/g, ''))) e.contact = "Valid 10-digit contact required";
    setAnimalErrors(e);
    return Object.keys(e).length === 0;
  }

  function submitAnimal(e) {
    e.preventDefault();
    if (!validateAnimal()) {
      showToast({ type: "error", message: "Please fix errors before submitting." });
      return;
    }
    // Dummy success (no backend)
    setAnimalForm({ type: "", desc: "", location: "", contact: "" });
    showToast({ type: "success", message: "Animal report submitted (dummy)." });
  }


 // Social Group form state
    const [socialGroupForm, setSocialGroupForm] = useState({
    title: "",
    type: "",
    desc: "",
    location: "",
    contact: "",
    });
    const [socialGroupErrors, setSocialGroupErrors] = useState({});

    // Social Group validation
    function validateSocialGroup() {
    const e = {};
    if (!socialGroupForm.title.trim()) e.title = "Title required";
    if (!socialGroupForm.type) e.type = "Type required";
    if (!socialGroupForm.desc.trim()) e.desc = "Description required";
    if (!socialGroupForm.location.trim()) e.location = "Location required";
    if (!/^\d{10}$/.test(socialGroupForm.contact.replace(/\s/g, ''))) e.contact = "Valid 10-digit contact required";
    setSocialGroupErrors(e);
    return Object.keys(e).length === 0;
    }

    function submitSocialGroup(e) {
    e.preventDefault();
    if (!validateSocialGroup()) {
        showToast({ type: "error", message: "Please fix errors before submitting." });
        return;
    }
    // Dummy success (no backend)
    setSocialGroupForm({ title: "", type: "", desc: "", location: "", contact: "" });
    showToast({ type: "success", message: "Social group created (dummy)." });
    }

// NGO form state
const [ngoForm, setNgoForm] = useState({
    title: "",
    focus: "",
    desc: "",
    location: "",
    contact: "",
    website: "",
    foundedOn: "",
    });

const [ngoErrors, setNgoErrors] = useState({});

// NGO suggestion validation
function validateNgo() {
    const e = {};
    if (!ngoForm.title.trim()) e.title = "NGO name required";
    if (!ngoForm.focus.trim()) e.focus = "Focus area required";
    if (!ngoForm.desc.trim()) e.desc = "Description required";
    if (!ngoForm.location.trim()) e.location = "Location required";
    if (!/^\d{10}$/.test(ngoForm.contact.replace(/\s/g, ''))) e.contact = "Valid 10-digit contact required";
    if (ngoForm.foundedOn && !/^\d{4}$/.test(ngoForm.foundedOn)) e.foundedOn = "Valid 4-digit year required";
    if (ngoForm.website && !/^https?:\/\/.+/.test(ngoForm.website)) e.website = "Valid URL required";
    setNgoErrors(e);
    return Object.keys(e).length === 0;
}

function submitNgo(e) {
    e.preventDefault();
    if (!validateNgo()) {
        showToast({ type: "error", message: "Please fix errors before submitting." });
        return;
    }
    // Dummy success (no backend)
    setNgoForm({ title: "", focus: "", desc: "", location: "", contact: "", website: "", foundedOn: "" });
    showToast({ type: "success", message: "NGO suggestion submitted (dummy)." });
}


  // Dummy common action buttons (shown under each tab header)
  const actionButtons = [
    { label: "📸 Upload Photo", icon: faCamera, action: () => showToast({ type: "info", message: "Upload (dummy)" }) },
    { label: "📍 View Nearby", icon: faMapMarkerAlt, action: () => showToast({ type: "info", message: "View Nearby (dummy)" }) },
    { label: "📝 Report Issue", icon: faClipboard, action: () => showToast({ type: "info", message: "Report Created (dummy)" }) },
    { label: "🙋 Volunteer", icon: faUserPlus, action: () => showToast({ type: "success", message: "Thanks for volunteering! (dummy)" }) },
    { label: "🔗 Share", icon: faShareAlt, action: () => showToast({ type: "info", message: "Shared (dummy)" }) },
    { label: "ℹ️ More Info", icon: faInfoCircle, action: () => showToast({ type: "info", message: "More info (dummy)" }) },
    { label: "📊 Statistics", icon: faChartBar, action: () => showToast({ type: "info", message: "Stats (dummy)" }) },
    { label: "⏱️ Report History", icon: faClock, action: () => showToast({ type: "info", message: "History (dummy)" }) },
  ];

  // Dummy datasets per tab
  const dummyWaste = [
    {
      id: "w1",
      title: "Leftover Meals - Community Kitchen",
      location: "Delhi, Janakpuri",
      estSavedMeals: 24,
      tags: ["Cooked", "Veg"],
      lastUpdated: "2025-10-10",
    },
    {
      id: "w2",
      title: "Surplus Groceries",
      location: "Mumbai, Bandra",
      estSavedMeals: 40,
      tags: ["Raw", "Dry"],
      lastUpdated: "2025-10-12",
    },
    {
      id: "w3",
      title: "Restaurant Unsold Salad",
      location: "Bengaluru, Koramangala",
      estSavedMeals: 12,
      tags: ["Cooked", "Salad"],
      lastUpdated: "2025-10-13",
    },
  ];

  const dummyDonations = [
    {
      id: "d1",
      requester: "Shanti Foundation",
      need: "Vegetarian meals (50)",
      location: "Pune, Shivajinagar",
      urgency: "High",
      contact: "98989XXXXX",
    },
    {
      id: "d2",
      requester: "Gram Seva",
      need: "Dry rations (100 packets)",
      location: "Lucknow, Gomti",
      urgency: "Medium",
      contact: "98000XXXXX",
    },
  ];

  const dummyViolations = [
    {
      id: "v1",
      type: "Open Sewage",
      location: "Kolkata, Park Street",
      severity: "High",
      reportedOn: "2025-10-11",
    },
    {
      id: "v2",
      type: "Illegal Dumping",
      location: "Hyderabad, Banjara Hills",
      severity: "Medium",
      reportedOn: "2025-10-09",
    },
  ];

  const dummyRoadTales = [
    {
      id: "r1",
      title: "Driver helped a stranded family",
      route: "NH-44",
      date: "2025-10-05",
      story: "Stopped and helped tow a bike and gave water to children.",
      likes: 120,
    },
  ];

  const dummyAnimals = [
    {
      id: "a1",
      type: "Street Dog",
      desc: "Injured, limping on left leg, friendly but scared",
      location: "Chennai, T Nagar",
      reportedOn: "2025-10-13",
      urgency: "High",
    },
    {
      id: "a2",
      type: "Lost Parrot",
      desc: "Green with red beak, answers to Miko, not aggressive",
      location: "Ahmedabad, Navrangpura",
      reportedOn: "2025-10-11",
      urgency: "Medium",
    },
    {
      id: "a3",
      type: "Stray Cat",
      desc: "Kitten found near market, very young, needs milk",
      location: "Mumbai, Dadar",
      reportedOn: "2025-10-14",
      urgency: "High",
    },
  ];

  const dummyGarbage = [
    {
      id: "g1",
      location: "Sector 22A, Chandigarh",
      qty: "5 bags",
      type: "Mixed (plastic + wet)",
      reportedOn: "2025-10-12",
    },
  ];

  const dummySocialGroups = [
    {
      id: "sg1",
      title: "Eco Cleaners United",
      location: "Mumbai, Marine Drive",
      members: 89,
      type: "Beach Clean-up",
      reportedOn: "2025-10-13",
    },
  ];

  const dummyNGOs = [
    {
      id: "n1",
      title: "Waste Warriors NGO",
      location: "Delhi, NCR",
      focus: "Plastic Reduction",
      contact: "9898989898",
      reportedOn: "2025-10-14",
    },
  ];

  // Convenience array for selecting dataset by tab index
  const datasets = [dummyWaste, dummyDonations, dummyViolations, dummyRoadTales, dummyAnimals, dummyGarbage, dummySocialGroups, dummyNGOs];
 
  // Filtered list (search + simple filter)
  const filteredList = useMemo(() => {
    const src = datasets[activeTab] || [];
    const q = query.trim().toLowerCase();
    let res = src.filter((item) => {
      return q === "" || JSON.stringify(item).toLowerCase().includes(q);
    });

    if (filter !== "All") {
      res = res.filter((r) => {
        // naive checks for common properties
        return (
          r.urgency === filter ||
          r.severity === filter ||
          r.type === filter ||
          r.tags?.includes(filter)
        );
      });
    }

    if (sortBy === "Newest") {
      res = res.sort((a, b) => {
        const aDate = a.lastUpdated || a.reportedOn || a.date || "";
        const bDate = b.lastUpdated || b.reportedOn || b.date || "";
        return aDate < bDate ? 1 : -1;
      });
    }
    if (sortBy === "Popular") {
      res = res.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    }

    return res;
  }, [activeTab, query, filter, sortBy]);

  // Utility: show toast and auto-hide
  function showToast(item) {
    setToast(item);
    setTimeout(() => setToast(null), 3000);
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 sm:p-6">
      <header className="flex justify-between mb-4">
        <div className="flex flex-row">
          <h1 className="text-lg font-bold text-yellow-200 tracking-wide text-left">
              <IndianFlagSVG /> RIM — Responsible India Movement
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => showToast({ type: "info", message: "No new notifications (dummy)" })}
            className="p-2 rounded-lg bg-gray-800/40 hover:bg-emerald-600 transition"
            aria-label="Notifications"
            title="Notifications"
          >
            <FontAwesomeIcon icon={faBell} />
          </button>

          <button
            onClick={() => showToast({ type: "info", message: "User panel (dummy)" })}
            className="p-2 rounded-lg bg-gray-800/40 hover:bg-emerald-600 transition"
            aria-label="Users"
            title="Users"
          >
            <FontAwesomeIcon icon={faUsers} />
          </button>
        </div>
      </header>

      {/* Tabs - responsive grid */}
      <nav className="mb-2">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 place-items-center">
          {rimTabs.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(i)}
              className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition ${
                activeTab === i ? "bg-emerald-600 text-black" : "bg-gray-800/40 text-gray-300"
              }`}
            >
              <FontAwesomeIcon icon={t.icon} />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Search + filter + sort */}
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-2 place-items-center">
        <div className="col-span-2 flex items-center gap-2 bg-gray-800/40 rounded-lg p-2">
          <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search in this tab..."
            className="bg-transparent outline-none w-full text-sm"
            aria-label="Search"
          />
        </div>

        <div className="flex gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-gray-800/40 h-12 p-2 rounded-2xl text-sm w-full">
            <option>All</option>
            <option>High</option>
            <option>Medium</option>
            <option>Low</option>
            <option>Cooked</option>
            <option>Raw</option>
            <option>Veg</option>
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-gray-800/40 h-12 p-2 rounded-2xl text-sm w-full">
            <option>Newest</option>
            <option>Popular</option>
          </select>
        </div>
      </div>

      {/* Active module summary */}
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-4">
        <div className="flex items-center justify-between bg-gray-800/30 p-3 rounded-2xl shadow-inner">
          <div>
            <div className="text-xs text-gray-400">Active Module</div>
            <div className="text-sm font-semibold">{rimTabs[activeTab].label}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-400">Impact Points</div>
            <div className="text-lg font-bold text-emerald-400">{Math.floor(Math.random() * 5000) + 100}</div>
          </div>
        </div>
      </motion.section>

      {/* Common action buttons (responsive grid: 2 cols mobile, 4 cols desktop) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 place-items-center">
        {actionButtons.map((b, i) => (
          <button
            key={i}
            onClick={b.action}
            className="flex items-center justify-center gap-2 px-2 py-2 bg-gray-800/40 hover:bg-emerald-600 rounded-xl text-xs transition"
          >
            <FontAwesomeIcon icon={b.icon} />
            <span className="truncate">{b.label}</span>
          </button>
        ))}
      </div>

      {/* Main content per tab */}
      <main className="space-y-4">
        {/* Tab 0: Reduce My Food Waste */}
        {activeTab === 0 && (
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">Nearby Surplus & Rescue Options</h2>
              <a className="text-xs text-gray-400 underline" href="#" onClick={(e) => e.preventDefault()}>
                See city heatmap
              </a>
            </div>

            <div className="space-y-3">
              {filteredList.map((w) => (
                <article key={w.id} className="bg-gray-800/40 p-3 rounded-2xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-sm font-semibold">{w.title}</div>
                      <div className="text-xs text-gray-400">{w.location} • Updated {w.lastUpdated}</div>
                      <div className="flex gap-2 mt-2 text-xs">
                        {w.tags.map((t) => (
                          <span key={t} className="bg-gray-700/60 px-2 py-1 rounded-full">{t}</span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <div className="text-sm font-bold text-emerald-400">{w.estSavedMeals} meals</div>

                      {/* buttons grid for this item */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 place-items-center">
                        <button onClick={() => showToast({ type: "success", message: "Rescued (dummy)" })} className="px-2 py-1 rounded-lg bg-emerald-600 text-black text-xs">Rescue</button>
                        <button onClick={() => showToast({ type: "info", message: "Shared (dummy)" })} className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs">Share</button>
                        <button onClick={() => showToast({ type: "info", message: "Navigating (dummy)" })} className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs">Navigate</button>
                        <button onClick={() => showToast({ type: "info", message: "Competitor info (dummy)" })} className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs">Competitor</button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                    <a href="#" onClick={(e) => e.preventDefault()} className="underline">Competitor: FoodCloud</a>
                    <a href="#" onClick={(e) => e.preventDefault()} className="underline">Competitor: Food Rescue</a>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Tab 1: Food Donation Request */}
        {activeTab === 1 && (
        <section>
            <h2 className="text-sm font-semibold mb-2">Open Donation Requests</h2>

            <div className="bg-gray-800/30 p-3 rounded-2xl mb-3">
            <div className="text-xs text-gray-400">Quick Actions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 place-items-center">
                <button
                onClick={() => showToast({ type: "success", message: "Request created (dummy)" })}
                className="px-3 py-2 rounded-xl bg-emerald-600 text-black text-xs"
                >
                Request Now
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Photo uploaded (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Upload Photo
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Guide opened (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                How to Request
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Nearby donors (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Nearby Donors
                </button>
            </div>
            </div>

            <div className="space-y-3 mb-3">
            {filteredList.map((d) => (
                <div key={d.id} className="bg-gray-800/40 p-3 rounded-2xl">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                    <div className="text-sm font-semibold">{d.requester}</div>
                    <div className="text-xs text-gray-400">
                        {d.need} • {d.location} • {d.deadline}
                    </div>
                    <div className="mt-1 text-xs text-gray-300">Posted: {d.postedOn}</div>
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2 min-w-0">
                    <div
                        className={`px-2 py-1 rounded-full text-xs ${
                        d.urgency === "High" ? "bg-red-600 text-white" : "bg-yellow-600 text-black"
                        }`}
                    >
                        {d.urgency}
                    </div>
                    <div className="text-xs text-gray-400">Contact: {d.contact}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full sm:w-auto place-items-center">
                        <button
                        onClick={() => showToast({ type: "success", message: "Volunteered (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-emerald-600 text-black text-xs"
                        >
                        Volunteer
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Navigating (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Navigate
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Shared (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Share
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Contacted (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Contact
                        </button>
                    </div>
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "ShareTheMeal (dummy)" });
                    }}
                    className="underline"
                    >
                    ShareTheMeal
                    </a>
                    <span>•</span>
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Feeding India (dummy)" });
                    }}
                    className="underline"
                    >
                    Feeding India
                    </a>
                    <span>•</span>
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Akshaya Patra (dummy)" });
                    }}
                    className="underline"
                    >
                    Akshaya Patra
                    </a>
                </div>
                </div>
            ))}
            </div>

            {/* Create Donation Request form */}
            <div className="bg-gray-800/30 p-3 rounded-2xl">
            <h3 className="text-sm font-semibold mb-2">Create Donation Request</h3>
            <form onSubmit={submitDonation} className="space-y-2">
                <div>
                <input
                    value={donationForm.orgName}
                    onChange={(e) => setDonationForm({ ...donationForm, orgName: e.target.value })}
                    placeholder="Organization / Your name"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {errors.orgName && <div className="text-xxs text-red-400 mt-1">{errors.orgName}</div>}
                </div>

                <div>
                <textarea
                    value={donationForm.need}
                    onChange={(e) => setDonationForm({ ...donationForm, need: e.target.value })}
                    placeholder="Need (e.g., Vegetarian meals (50), Dry rations (100 packets))"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                    rows={2}
                />
                {errors.need && <div className="text-xxs text-red-400 mt-1">{errors.need}</div>}
                </div>

                <div className="grid grid-cols-2 gap-2  place-items-center">
                <input
                    value={donationForm.quantity}
                    onChange={(e) => setDonationForm({ ...donationForm, quantity: e.target.value })}
                    placeholder="Quantity"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {errors.quantity && <div className="text-xxs text-red-400 mt-1 col-span-2">{errors.quantity}</div>}
                <select
                    value={donationForm.urgency || "Medium"}
                    onChange={(e) => setDonationForm({ ...donationForm, urgency: e.target.value })}
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                </select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                <input
                    value={donationForm.contact}
                    onChange={(e) => setDonationForm({ ...donationForm, contact: e.target.value })}
                    placeholder="Contact number"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {errors.contact && <div className="text-xxs text-red-400 mt-1 col-span-2">{errors.contact}</div>}
                <input
                    value={donationForm.location}
                    onChange={(e) => setDonationForm({ ...donationForm, location: e.target.value })}
                    placeholder="Location (City, Area)"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {errors.location && <div className="text-xxs text-red-400 mt-1 col-span-2">{errors.location}</div>}
                </div>

                <div className="flex items-center gap-2">
                <button type="submit" className="flex-1 px-4 py-2 rounded-2xl bg-emerald-600 text-black font-semibold">
                    Submit Request
                </button>
                <button
                    type="button"
                    onClick={() => setDonationForm({ orgName: "", need: "", quantity: "", contact: "", location: "", urgency: "" })}
                    className="px-3 py-2 rounded-2xl bg-gray-700/60"
                >
                    Reset
                </button>
                </div>

                <div className="text-xs text-gray-400">Competitors: ShareTheMeal • Feeding India • Akshaya Patra</div>
            </form>
            </div>
        </section>
        )}

        {/* Tab 2: I See Violation */}
        {activeTab === 2 && (
        <section>
            <h2 className="text-sm font-semibold mb-2">Report a Civic Violation</h2>

            <div className="bg-gray-800/30 p-3 rounded-2xl mb-3">
            <div className="text-xs text-gray-400">Quick Actions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 place-items-center">
                <button
                onClick={() => showToast({ type: "success", message: "Report submitted (dummy)" })}
                className="px-3 py-2 rounded-xl bg-red-600 text-black text-xs"
                >
                Report Now
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Draft saved (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Save Draft
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Photo uploaded (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Upload Photo
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Guide opened (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Guide: How to Report
                </button>
            </div>
            </div>

            <div className="space-y-3 mb-3">
            {filteredList.map((v) => (
                <div key={v.id} className="bg-gray-800/40 p-3 rounded-2xl">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                    <div className="text-sm font-semibold">{v.type}</div>
                    <div className="text-xs text-gray-400">{v.location} • Reported {v.reportedOn}</div>
                    <div className="mt-1 text-xs text-gray-300">{v.desc}</div>
                    {v.status && <div className="mt-1 text-xs text-emerald-400">Status: {v.status}</div>}
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2 min-w-0">
                    <div
                        className={`px-2 py-1 rounded-full text-xs ${
                        v.severity === "High" ? "bg-red-600 text-white" : "bg-yellow-600 text-black"
                        }`}
                    >
                        {v.severity}
                    </div>
                    {v.assignedTo && <div className="text-xs text-gray-400">Assigned: {v.assignedTo}</div>}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full sm:w-auto place-items-center">
                        <button
                        onClick={() => showToast({ type: "info", message: "Support requested (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Support
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Comment added (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Comment
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Navigating (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Navigate
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Shared (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Share
                        </button>
                    </div>
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "MyGov (dummy)" });
                    }}
                    className="underline"
                    >
                    MyGov
                    </a>
                    <span>•</span>
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Local civic apps (dummy)" });
                    }}
                    className="underline"
                    >
                    Local Civic Apps
                    </a>
                </div>
                </div>
            ))}
            </div>

            {/* Create Violation Report form */}
            <div className="bg-gray-800/30 p-3 rounded-2xl">
            <h3 className="text-sm font-semibold mb-2">Report New Violation</h3>
            <form onSubmit={submitViolation} className="space-y-2">
                <div>
                <select
                    value={violationForm.type}
                    onChange={(e) => setViolationForm({ ...violationForm, type: e.target.value })}
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                >
                    <option value="">Select Type</option>
                    <option value="Open Sewage">Open Sewage</option>
                    <option value="Illegal Dumping">Illegal Dumping</option>
                    <option value="Encroachment">Encroachment</option>
                    <option value="Noise Pollution">Noise Pollution</option>
                    <option value="Other">Other</option>
                </select>
                {violationErrors.type && <div className="text-xxs text-red-400 mt-1">{violationErrors.type}</div>}
                </div>

                <div>
                <textarea
                    value={violationForm.desc}
                    onChange={(e) => setViolationForm({ ...violationForm, desc: e.target.value })}
                    placeholder="Description (details of the violation)"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                    rows={2}
                />
                {violationErrors.desc && <div className="text-xxs text-red-400 mt-1">{violationErrors.desc}</div>}
                </div>

                <div className="grid grid-cols-2 gap-2">
                <select
                    value={violationForm.severity}
                    onChange={(e) => setViolationForm({ ...violationForm, severity: e.target.value })}
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                >
                    <option value="">Select Severity</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                </select>
                <input
                    value={violationForm.location}
                    onChange={(e) => setViolationForm({ ...violationForm, location: e.target.value })}
                    placeholder="Location (City, Area)"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                </div>

                <div>
                <input
                    value={violationForm.contact}
                    onChange={(e) => setViolationForm({ ...violationForm, contact: e.target.value })}
                    placeholder="Your Contact Number"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {violationErrors.contact && <div className="text-xxs text-red-400 mt-1">{violationErrors.contact}</div>}
                </div>

                <div className="flex items-center gap-2">
                <button type="submit" className="flex-1 px-4 py-2 rounded-2xl bg-red-600 text-white font-semibold">
                    Submit Report
                </button>
                <button
                    type="button"
                    onClick={() => setViolationForm({ type: "", desc: "", location: "", severity: "", contact: "" })}
                    className="px-3 py-2 rounded-2xl bg-gray-700/60"
                >
                    Reset
                </button>
                </div>
            </form>
            </div>
        </section>
        )}

        {/* Tab 3: When I Was on Road */}
        {activeTab === 3 && (
        <section>
            <h2 className="text-sm font-semibold mb-2">Road Tales — Stories from Commuters</h2>

            <div className="bg-gray-800/30 p-3 rounded-2xl mb-3">
            <div className="text-xs text-gray-400">Quick Actions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 place-items-center">
                <button
                onClick={() => showToast({ type: "success", message: "Story shared (dummy)" })}
                className="px-3 py-2 rounded-xl bg-emerald-600 text-black text-xs"
                >
                Share Tale
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "New story started (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Write New
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Photo added (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Add Photo
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Guide opened (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                How to Share
                </button>
            </div>
            </div>

            <div className="space-y-3 mb-3">
            {filteredList.map((r) => (
                <article key={r.id} className="bg-gray-800/40 p-3 rounded-2xl">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                    <div className="text-sm font-semibold">{r.title}</div>
                    <div className="text-xs text-gray-400">
                        {r.route} • {r.date} • By {r.author}
                    </div>
                    <p className="mt-2 text-xs text-gray-300">{r.story}</p>
                    {r.comments > 0 && <div className="mt-1 text-xs text-gray-400">{r.comments} comments</div>}
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2 min-w-0">
                    <div className="text-sm font-bold text-emerald-400">{r.likes} ❤</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full sm:w-auto">
                        <button
                        onClick={() => showToast({ type: "info", message: "Shared (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Share
                        </button>
                        <button
                        onClick={() => showToast({ type: "success", message: "Appreciated (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-emerald-600 text-black text-xs"
                        >
                        Appreciate
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Comment added (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Comment
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Navigating (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Navigate
                        </button>
                    </div>
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Road Safety Apps (dummy)" });
                    }}
                    className="underline"
                    >
                    Road Safety Apps
                    </a>
                    <span>•</span>
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Commuter Forums (dummy)" });
                    }}
                    className="underline"
                    >
                    Commuter Forums
                    </a>
                </div>
                </article>
            ))}
        </div>

        {/* Tab 4: Street Dogs / Pet Animals */}
        {activeTab === 4 && (
        <section>
            <h2 className="text-sm font-semibold mb-2">Animal Reports & Help</h2>

            <div className="bg-gray-800/30 p-3 rounded-2xl mb-3">
            <div className="text-xs text-gray-400">Quick Actions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 place-items-center">
                <button
                onClick={() => showToast({ type: "success", message: "Report submitted (dummy)" })}
                className="px-3 py-2 rounded-xl bg-emerald-600 text-black text-xs"
                >
                Report New
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Photo uploaded (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Upload Photo
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Guide opened (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                How to Help
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Nearby shelters (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Nearby Shelters
                </button>
            </div>
            </div>

            <div className="space-y-3 mb-3">
            {filteredList.map((a) => (
                <div key={a.id} className="bg-gray-800/40 p-3 rounded-2xl">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                    <div className="text-sm font-semibold">{a.type}</div>
                    <div className="text-xs text-gray-400">{a.location} • {a.reportedOn}</div>
                    <div className="mt-2 text-xs text-gray-300">{a.desc}</div>
                    {a.status && <div className="mt-1 text-xs text-emerald-400">Status: {a.status}</div>}
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2 min-w-0">
                    <div
                        className={`px-2 py-1 rounded-full text-xs ${
                        a.urgency === "High" ? "bg-red-600 text-white" : "bg-yellow-600 text-black"
                        }`}
                    >
                        {a.urgency}
                    </div>
                    {a.assignedTo && <div className="text-xs text-gray-400">Assigned: {a.assignedTo}</div>}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full sm:w-auto">
                        <button
                        onClick={() => showToast({ type: "success", message: "Volunteered (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-emerald-600 text-black text-xs"
                        >
                        Volunteer
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Vet requested (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Request Vet
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Navigating (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Navigate
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Shared (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Share
                        </button>
                    </div>
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "People For Animals (dummy)" });
                    }}
                    className="underline"
                    >
                    People For Animals
                    </a>
                    <span>•</span>
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Local NGOs (dummy)" });
                    }}
                    className="underline"
                    >
                    Local NGOs
                    </a>
                </div>
                </div>
            ))}
            </div>

            {/* Create Animal Report form */}
            <div className="bg-gray-800/30 p-3 rounded-2xl">
            <h3 className="text-sm font-semibold mb-2">Report New Animal Sighting</h3>
            <form onSubmit={submitAnimal} className="space-y-2">
                <div>
                <select
                    value={animalForm.type}
                    onChange={(e) => setAnimalForm({ ...animalForm, type: e.target.value })}
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                >
                    <option value="">Select Type</option>
                    <option value="Street Dog">Street Dog</option>
                    <option value="Stray Cat">Stray Cat</option>
                    <option value="Lost Pet">Lost Pet</option>
                    <option value="Wild Animal">Wild Animal</option>
                </select>
                {animalErrors.type && <div className="text-xxs text-red-400 mt-1">{animalErrors.type}</div>}
                </div>

                <div>
                <textarea
                    value={animalForm.desc}
                    onChange={(e) => setAnimalForm({ ...animalForm, desc: e.target.value })}
                    placeholder="Description (condition, color, behavior, etc.)"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                    rows={2}
                />
                {animalErrors.desc && <div className="text-xxs text-red-400 mt-1">{animalErrors.desc}</div>}
                </div>

                <div className="grid grid-cols-2 gap-2">
                <select
                    value={animalForm.urgency || "Medium"}
                    onChange={(e) => setAnimalForm({ ...animalForm, urgency: e.target.value })}
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                </select>
                <input
                    value={animalForm.location}
                    onChange={(e) => setAnimalForm({ ...animalForm, location: e.target.value })}
                    placeholder="Location (City, Area)"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                </div>

                <div>
                <input
                    value={animalForm.contact}
                    onChange={(e) => setAnimalForm({ ...animalForm, contact: e.target.value })}
                    placeholder="Your Contact Number"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {animalErrors.contact && <div className="text-xxs text-red-400 mt-1">{animalErrors.contact}</div>}
                </div>

                <div className="flex items-center gap-2">
                <button type="submit" className="flex-1 px-4 py-2 rounded-2xl bg-emerald-600 text-black font-semibold">
                    Submit Report
                </button>
                <button
                    type="button"
                    onClick={() => setAnimalForm({ type: "", desc: "", location: "", contact: "", urgency: "" })}
                    className="px-3 py-2 rounded-2xl bg-gray-700/60"
                >
                    Reset
                </button>
                </div>
            </form>
            </div>
        </section>
        )}


            {/* Create Road Tale form */}
            <div className="bg-gray-800/30 p-3 rounded-2xl">
            <h3 className="text-sm font-semibold mb-2">Share Your Road Tale</h3>
            <form onSubmit={submitRoadTale} className="space-y-2">
                <div>
                <input
                    value={roadTaleForm.title}
                    onChange={(e) => setRoadTaleForm({ ...roadTaleForm, title: e.target.value })}
                    placeholder="Title of your story"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {roadTaleErrors.title && <div className="text-xxs text-red-400 mt-1">{roadTaleErrors.title}</div>}
                </div>

                <div>
                <input
                    value={roadTaleForm.route}
                    onChange={(e) => setRoadTaleForm({ ...roadTaleForm, route: e.target.value })}
                    placeholder="Route (e.g., NH-44)"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {roadTaleErrors.route && <div className="text-xxs text-red-400 mt-1">{roadTaleErrors.route}</div>}
                </div>

                <div>
                <textarea
                    value={roadTaleForm.story}
                    onChange={(e) => setRoadTaleForm({ ...roadTaleForm, story: e.target.value })}
                    placeholder="Your story (what happened, how you helped, etc.)"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                    rows={3}
                />
                {roadTaleErrors.story && <div className="text-xxs text-red-400 mt-1">{roadTaleErrors.story}</div>}
                </div>

                <div className="grid grid-cols-2 gap-2">
                <input
                    value={roadTaleForm.author}
                    onChange={(e) => setRoadTaleForm({ ...roadTaleForm, author: e.target.value })}
                    placeholder="Your Name"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                <input
                    value={roadTaleForm.contact}
                    onChange={(e) => setRoadTaleForm({ ...roadTaleForm, contact: e.target.value })}
                    placeholder="Contact (optional)"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                </div>

                <div className="flex items-center gap-2">
                <button type="submit" className="flex-1 px-4 py-2 rounded-2xl bg-emerald-600 text-black font-semibold">
                    Share Tale
                </button>
                <button
                    type="button"
                    onClick={() => setRoadTaleForm({ title: "", route: "", story: "", author: "", contact: "" })}
                    className="px-3 py-2 rounded-2xl bg-gray-700/60"
                >
                    Reset
                </button>
                </div>
            </form>
            </div>
        </section>
        )}

        {/* Tab 5: I Saw Garbage */}
        {activeTab === 5 && (
        <section>
            <h2 className="text-sm font-semibold mb-2">Garbage Sightings</h2>

            <div className="bg-gray-800/30 p-3 rounded-2xl mb-3">
            <div className="text-xs text-gray-400">Quick Actions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 place-items-center">
                <button
                onClick={() => showToast({ type: "success", message: "Report submitted (dummy)" })}
                className="px-3 py-2 rounded-xl bg-emerald-600 text-black text-xs"
                >
                Report Now
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Photo uploaded (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Upload Photo
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Guide opened (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                How to Report
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Nearby collection points (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Nearby Points
                </button>
            </div>
            </div>

            <div className="space-y-3 mb-3">
            {filteredList.map((g) => (
                <div key={g.id} className="bg-gray-800/40 p-3 rounded-2xl">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                    <div className="text-sm font-semibold">{g.location}</div>
                    <div className="text-xs text-gray-400">{g.qty} • {g.type} • Reported {g.reportedOn}</div>
                    <div className="mt-1 text-xs text-gray-300">{g.desc}</div>
                    {g.status && <div className="mt-1 text-xs text-emerald-400">Status: {g.status}</div>}
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2 min-w-0">
                    <div
                        className={`px-2 py-1 rounded-full text-xs ${
                        g.severity === "High" ? "bg-red-600 text-white" : "bg-yellow-600 text-black"
                        }`}
                    >
                        {g.severity}
                    </div>
                    {g.assignedTo && <div className="text-xs text-gray-400">Assigned: {g.assignedTo}</div>}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full sm:w-auto place-items-center">
                        <button
                        onClick={() => showToast({ type: "success", message: "Marked cleared (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-emerald-600 text-black text-xs"
                        >
                        Mark Cleared
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Assigned (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Assign to Civic Body
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Uploaded (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Upload
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Shared (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Share
                        </button>
                    </div>
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Swachh Bharat (dummy)" });
                    }}
                    className="underline"
                    >
                    Swachh Bharat
                    </a>
                    <span>•</span>
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Local Municipal Apps (dummy)" });
                    }}
                    className="underline"
                    >
                    Local Municipal Apps
                    </a>
                </div>
                </div>
            ))}
            </div>

            {/* Create Garbage Report form */}
            <div className="bg-gray-800/30 p-3 rounded-2xl">
            <h3 className="text-sm font-semibold mb-2">Report New Garbage Sighting</h3>
            <form onSubmit={submitGarbage} className="space-y-2">
                <div>
                <select
                    value={garbageForm.type}
                    onChange={(e) => setGarbageForm({ ...garbageForm, type: e.target.value })}
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                >
                    <option value="">Select Type</option>
                    <option value="Mixed (plastic + wet)">Mixed (plastic + wet)</option>
                    <option value="Plastic Waste">Plastic Waste</option>
                    <option value="Organic Waste">Organic Waste</option>
                    <option value="Construction Debris">Construction Debris</option>
                    <option value="Other">Other</option>
                </select>
                {garbageErrors.type && <div className="text-xxs text-red-400 mt-1">{garbageErrors.type}</div>}
                </div>

                <div>
                <input
                    value={garbageForm.qty}
                    onChange={(e) => setGarbageForm({ ...garbageForm, qty: e.target.value })}
                    placeholder="Quantity (e.g., 5 bags)"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {garbageErrors.qty && <div className="text-xxs text-red-400 mt-1">{garbageErrors.qty}</div>}
                </div>

                <div>
                <textarea
                    value={garbageForm.desc}
                    onChange={(e) => setGarbageForm({ ...garbageForm, desc: e.target.value })}
                    placeholder="Description (location details, photos if uploaded, etc.)"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                    rows={2}
                />
                {garbageErrors.desc && <div className="text-xxs text-red-400 mt-1">{garbageErrors.desc}</div>}
                </div>

                <div className="grid grid-cols-2 gap-2">
                <select
                    value={garbageForm.severity}
                    onChange={(e) => setGarbageForm({ ...garbageForm, severity: e.target.value })}
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                >
                    <option value="">Select Severity</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                </select>
                <input
                    value={garbageForm.location}
                    onChange={(e) => setGarbageForm({ ...garbageForm, location: e.target.value })}
                    placeholder="Exact Location"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                </div>

                <div>
                <input
                    value={garbageForm.contact}
                    onChange={(e) => setGarbageForm({ ...garbageForm, contact: e.target.value })}
                    placeholder="Your Contact Number"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {garbageErrors.contact && <div className="text-xxs text-red-400 mt-1">{garbageErrors.contact}</div>}
                </div>

                <div className="flex items-center gap-2">
                <button type="submit" className="flex-1 px-4 py-2 rounded-2xl bg-emerald-600 text-black font-semibold">
                    Submit Report
                </button>
                <button
                    type="button"
                    onClick={() => setGarbageForm({ type: "", qty: "", desc: "", location: "", severity: "", contact: "" })}
                    className="px-3 py-2 rounded-2xl bg-gray-700/60"
                >
                    Reset
                </button>
                </div>
            </form>
            </div>
        </section>
        )}

        {/* Tab 6: Social Group */}
        {activeTab === 6 && (
        <section>
            <h2 className="text-sm font-semibold mb-2">Social Groups</h2>

            <div className="bg-gray-800/30 p-3 rounded-2xl mb-3">
            <div className="text-xs text-gray-400">Quick Actions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 place-items-center">
                <button
                onClick={() => showToast({ type: "success", message: "Group joined (dummy)" })}
                className="px-3 py-2 rounded-xl bg-emerald-600 text-black text-xs"
                >
                Join Group
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "New group created (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Create Group
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Nearby groups (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Nearby Groups
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Guide opened (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                How to Join
                </button>
            </div>
            </div>

            <div className="space-y-3 mb-3">
            {filteredList.map((sg) => (
                <div key={sg.id} className="bg-gray-800/40 p-3 rounded-2xl">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                    <div className="text-sm font-semibold">{sg.title}</div>
                    <div className="text-xs text-gray-400">
                        {sg.location} • {sg.members} members • {sg.type}
                    </div>
                    <div className="mt-1 text-xs text-gray-300">{sg.desc}</div>
                    <div className="mt-1 text-xs text-gray-400">Created: {sg.createdOn}</div>
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2 min-w-0">
                    {sg.upcomingEvents > 0 && <div className="text-xs text-emerald-400">{sg.upcomingEvents} upcoming events</div>}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full sm:w-auto place-items-center">
                        <button
                        onClick={() => showToast({ type: "success", message: "Joined group (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-emerald-600 text-black text-xs"
                        >
                        Join
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Event created (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Create Event
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Contacted (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Contact
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Shared (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Share
                        </button>
                    </div>
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Facebook Groups (dummy)" });
                    }}
                    className="underline"
                    >
                    Facebook Groups
                    </a>
                    <span>•</span>
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Meetup.com (dummy)" });
                    }}
                    className="underline"
                    >
                    Meetup.com
                    </a>
                </div>
                </div>
            ))}
            </div>

            {/* Create Social Group form */}
            <div className="bg-gray-800/30 p-3 rounded-2xl">
            <h3 className="text-sm font-semibold mb-2">Create New Social Group</h3>
            <form onSubmit={submitSocialGroup} className="space-y-2">
                <div>
                <input
                    value={socialGroupForm.title}
                    onChange={(e) => setSocialGroupForm({ ...socialGroupForm, title: e.target.value })}
                    placeholder="Group Title"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {socialGroupErrors.title && <div className="text-xxs text-red-400 mt-1">{socialGroupErrors.title}</div>}
                </div>

                <div>
                <select
                    value={socialGroupForm.type}
                    onChange={(e) => setSocialGroupForm({ ...socialGroupForm, type: e.target.value })}
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                >
                    <option value="">Select Type</option>
                    <option value="Beach Clean-up">Beach Clean-up</option>
                    <option value="Tree Planting">Tree Planting</option>
                    <option value="Food Distribution">Food Distribution</option>
                    <option value="Other">Other</option>
                </select>
                {socialGroupErrors.type && <div className="text-xxs text-red-400 mt-1">{socialGroupErrors.type}</div>}
                </div>

                <div>
                <textarea
                    value={socialGroupForm.desc}
                    onChange={(e) => setSocialGroupForm({ ...socialGroupForm, desc: e.target.value })}
                    placeholder="Description (focus, activities, etc.)"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                    rows={2}
                />
                {socialGroupErrors.desc && <div className="text-xxs text-red-400 mt-1">{socialGroupErrors.desc}</div>}
                </div>

                <div className="grid grid-cols-2 gap-2">
                <input
                    value={socialGroupForm.location}
                    onChange={(e) => setSocialGroupForm({ ...socialGroupForm, location: e.target.value })}
                    placeholder="Location (City, Area)"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                <input
                    value={socialGroupForm.contact}
                    onChange={(e) => setSocialGroupForm({ ...socialGroupForm, contact: e.target.value })}
                    placeholder="Contact Number"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                </div>

                <div className="flex items-center gap-2">
                <button type="submit" className="flex-1 px-4 py-2 rounded-2xl bg-emerald-600 text-black font-semibold">
                    Create Group
                </button>
                <button
                    type="button"
                    onClick={() => setSocialGroupForm({ title: "", type: "", desc: "", location: "", contact: "" })}
                    className="px-3 py-2 rounded-2xl bg-gray-700/60"
                >
                    Reset
                </button>
                </div>
            </form>
            </div>
        </section>
        )}

        {/* Tab 7: NGOs */}
        {activeTab === 7 && (
        <section>
            <h2 className="text-sm font-semibold mb-2">NGOs Directory</h2>

            <div className="bg-gray-800/30 p-3 rounded-2xl mb-3">
            <div className="text-xs text-gray-400">Quick Actions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 place-items-center">
                <button
                onClick={() => showToast({ type: "success", message: "Donation initiated (dummy)" })}
                className="px-3 py-2 rounded-xl bg-emerald-600 text-black text-xs"
                >
                Donate Now
                </button>
                <button
                onClick={() => showToast({ type: "success", message: "Volunteer registered (dummy)" })}
                className="px-3 py-2 rounded-xl bg-emerald-600 text-black text-xs"
                >
                Volunteer
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "NGO search (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                Search NGOs
                </button>
                <button
                onClick={() => showToast({ type: "info", message: "Guide opened (dummy)" })}
                className="px-3 py-2 rounded-xl bg-gray-700/60 text-xs"
                >
                How to Support
                </button>
            </div>
            </div>

            <div className="space-y-3 mb-3">
            {filteredList.map((ngo) => (
                <div key={ngo.id} className="bg-gray-800/40 p-3 rounded-2xl">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                    <div className="text-sm font-semibold">{ngo.title}</div>
                    <div className="text-xs text-gray-400">{ngo.location} • {ngo.focus}</div>
                    <div className="mt-1 text-xs text-gray-300">{ngo.desc}</div>
                    <div className="mt-1 text-xs text-gray-400">Founded: {ngo.foundedOn} • Contact: {ngo.contact}</div>
                    {ngo.website && <div className="mt-1 text-xs text-blue-400 underline">Website: {ngo.website}</div>}
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2 min-w-0">
                    <div className="text-xs text-emerald-400">{ngo.impact} impact points</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full sm:w-auto place-items-center">
                        <button
                        onClick={() => showToast({ type: "success", message: "Donated (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-emerald-600 text-black text-xs"
                        >
                        Donate
                        </button>
                        <button
                        onClick={() => showToast({ type: "success", message: "Volunteered (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-emerald-600 text-black text-xs"
                        >
                        Volunteer
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Website opened (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Website
                        </button>
                        <button
                        onClick={() => showToast({ type: "info", message: "Shared (dummy)" })}
                        className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs"
                        >
                        Share
                        </button>
                    </div>
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "WWF (dummy)" });
                    }}
                    className="underline"
                    >
                    WWF
                    </a>
                    <span>•</span>
                    <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        showToast({ type: "info", message: "Greenpeace (dummy)" });
                    }}
                    className="underline"
                    >
                    Greenpeace
                    </a>
                </div>
                </div>
            ))}
            </div>

            {/* Suggest New NGO form */}
            <div className="bg-gray-800/30 p-3 rounded-2xl">
            <h3 className="text-sm font-semibold mb-2">Suggest New NGO</h3>
            <form onSubmit={submitNgo} className="space-y-2">
                <div>
                <input
                    value={ngoForm.title}
                    onChange={(e) => setNgoForm({ ...ngoForm, title: e.target.value })}
                    placeholder="NGO Name"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {ngoErrors.title && <div className="text-xxs text-red-400 mt-1">{ngoErrors.title}</div>}
                </div>

                <div>
                <input
                    value={ngoForm.focus}
                    onChange={(e) => setNgoForm({ ...ngoForm, focus: e.target.value })}
                    placeholder="Focus Area (e.g., Plastic Reduction)"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                {ngoErrors.focus && <div className="text-xxs text-red-400 mt-1">{ngoErrors.focus}</div>}
                </div>

                <div>
                <textarea
                    value={ngoForm.desc}
                    onChange={(e) => setNgoForm({ ...ngoForm, desc: e.target.value })}
                    placeholder="Description"
                    className="w-full p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                    rows={2}
                />
                {ngoErrors.desc && <div className="text-xxs text-red-400 mt-1">{ngoErrors.desc}</div>}
                </div>

                <div className="grid grid-cols-2 gap-2">
                <input
                    value={ngoForm.location}
                    onChange={(e) => setNgoForm({ ...ngoForm, location: e.target.value })}
                    placeholder="Location (City, Area)"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                <input
                    value={ngoForm.contact}
                    onChange={(e) => setNgoForm({ ...ngoForm, contact: e.target.value })}
                    placeholder="Contact Number"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                </div>

                <div className="grid grid-cols-2 gap-2">
                <input
                    value={ngoForm.website}
                    onChange={(e) => setNgoForm({ ...ngoForm, website: e.target.value })}
                    placeholder="Website URL"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                <input
                    value={ngoForm.foundedOn}
                    onChange={(e) => setNgoForm({ ...ngoForm, foundedOn: e.target.value })}
                    placeholder="Founded Year"
                    className="p-2 rounded-xl bg-gray-800/40 text-sm outline-none"
                />
                </div>

                <div className="flex items-center gap-2">
                <button type="submit" className="flex-1 px-4 py-2 rounded-2xl bg-emerald-600 text-black font-semibold">
                    Suggest NGO
                </button>
                <button
                    type="button"
                    onClick={() => setNgoForm({ title: "", focus: "", desc: "", location: "", contact: "", website: "", foundedOn: "" })}
                    className="px-3 py-2 rounded-2xl bg-gray-700/60"
                >
                    Reset
                </button>
                </div>
            </form>
            </div>
        </section>
        )}
      </main>

      {/* Fixed actions row - integrated at bottom of main content */}
      <div className="mt-6 bg-gray-800/60 backdrop-blur-xl border border-gray-700/40 rounded-3xl p-3 shadow-lg flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Left group — info actions */}
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto place-items-center">
          <button
            onClick={() => showToast({ type: "info", message: "How RIM Works (dummy)" })}
            className="flex items-center justify-center gap-2 bg-gray-700/50 hover:bg-emerald-600 hover:text-black px-3 py-2 rounded-2xl text-xs sm:text-sm transition-all"
          >
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>How RIM Works</span>
          </button>

          <button
            onClick={() => showToast({ type: "warning", message: "Report a Problem (dummy)" })}
            className="flex items-center justify-center gap-2 bg-gray-700/50 hover:bg-red-600 hover:text-black px-3 py-2 rounded-2xl text-xs sm:text-sm transition-all"
          >
            <FontAwesomeIcon icon={faFlag} />
            <span>Report Issue</span>
          </button>
        </div>

        {/* Right group — primary CTAs */}
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto place-items-center">
          <button
            onClick={() => showToast({ type: "success", message: "Create new (dummy)" })}
            className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-black font-semibold px-4 py-2 rounded-2xl text-xs sm:text-sm transition-all"
          >
            + New
          </button>

          <button
            onClick={() => showToast({ type: "info", message: "Export (dummy)" })}
            className="flex items-center justify-center gap-2 bg-gray-700/50 hover:bg-gray-600 px-4 py-2 rounded-2xl text-xs sm:text-sm transition-all"
          >
            <FontAwesomeIcon icon={faChartBar} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`fixed left-1/2 transform -translate-x-1/2 bottom-4 px-4 py-2 rounded-2xl text-sm font-semibold ${
            toast.type === "success" ? "bg-emerald-600 text-black" : toast.type === "error" ? "bg-red-600 text-white" : "bg-gray-200 text-black"
          }`}
        >
          {toast.message}
        </motion.div>
      )}

      {/* Footer */}
      <footer className="mt-8 text-center text-xs text-gray-500">
        <div>© {new Date().getFullYear()} Think5Whys • Responsible India Movement</div>
        <div className="text-xs text-gray-400">Data shown is dummy.</div>
      </footer>
    </div>
  );
}